/**
 * #about-techgala（Aboutページ）SP専用: 「背景固定」
 *
 * #about-techgala（セクション自身）にはブロックエディタの「背景画像」機能で
 * 背景画像（グラデーション）がインラインスタイルとして設定されている。この
 * 画像を、セクション自身のレイアウト（position等）を変更したりせずに、
 * 見た目上だけ「その場に留まる／解除する」ようにする。
 *
 * 通常時は、背景画像はセクション自身の上端を基準に配置されており（CSS側で
 * background-position: center top !important; を指定）、ページを
 * スクロールするとセクションが動くのに合わせて画像も一緒に動く
 * （＝普通にスクロールする）。
 *
 * 固定の実現方法について：
 * 当初は background-attachment: fixed をセクション自身に直接切り替える
 * 方式にしていたが、iOS Safari（モバイルWebKit）では background-attachment:
 * fixed が正しく機能しない（無視される、または固定した瞬間の位置がずれる／
 * 画像の別の部分が見えてしまう）という既知の制限があり、実機Safariで
 * 症状が発生した。そのため、固定中だけ表示する position: fixed の
 * オーバーレイ要素（body直下に1つだけ生成）に背景画像を移し、これで
 * 「画面に対して静止して見える」状態を作る（position: fixedはiOS Safariでも
 * 確実に機能する）。固定中はセクション自身の背景画像を非表示にする
 * （background-image: none）ことで、二重に表示されることはない。
 *
 * .scroll-area の上端がヘッダー下端（+ ENGAGE_MARGIN分手前）に到達した
 * 瞬間だけ、
 *   1. その瞬間に画面上で実際に見えている位置を計算し、
 *   2. オーバーレイ要素にその位置で背景画像を表示し、セクション自身の
 *      背景画像を隠す
 * ことで、以後はオーバーレイがその背景を画面に対して常に同じ位置に固定して
 * 描画する（ネイティブなposition: fixedなので、スクロール中にJS側で毎回
 * 位置を計算し直す必要がない。むしろ毎フレーム計算し直すと描画が追いつかず
 * 背景がガタつく／震えるように見えることが分かったため、固定中は一切
 * 再計算しない）。
 *
 * .scroll-area の下端がヘッダー下端を通り過ぎた瞬間、今度は逆に
 *   1. 固定していた画面上の位置を保ったまま
 *   2. オーバーレイを隠してセクション自身の背景画像を復元し、その時点の
 *      セクション自身の位置を基準にした値へ変換してbackground-position-yに
 *      設定する
 * ことで、解除の瞬間もジャンプせず、以後は通常どおりページと一緒に背景が
 * 流れていく。
 *
 * .scroll-area自体・動画（直前の.wp-block-embed）はこの仕組みの対象外で、
 * 常に通常のページスクロールでそのまま画面を流れていく（内部スクロール・
 * スクロールバー・position変更は一切行わない）。
 */
( function () {
	const SP_MAX_WIDTH = 1019; // src/scss/abstracts/_variables.scss の $breakpoint-sm と合わせる

	const section = document.querySelector( '#about-techgala.sec-about-techgala' );
	if ( ! section ) {
		return;
	}

	const scrollArea = section.querySelector( '.scroll-area' );
	if ( ! scrollArea ) {
		return;
	}

	function getHeaderHeight() {
		const value = parseFloat(
			getComputedStyle( document.documentElement ).getPropertyValue( '--header-height' )
		);
		return Number.isFinite( value ) && value > 0 ? value : 60;
	}

	function isSpBreakpoint() {
		return window.innerWidth <= SP_MAX_WIDTH;
	}

	// .scroll-areaの上端がヘッダー下端よりこの分だけ上（＝手前）にある時点で
	// 固定を開始する（値を大きくするほど早いタイミングで固定される）。
	const ENGAGE_MARGIN = 40;

	let pinned = false;

	// ページ読み込み時点（リロード直後を含む）で、まだ一度もこのスクリプトに
	// よる判定を行っていないことを示すフラグ。リロード時にスクロール位置が
	// すでに固定範囲より下（＝本来なら固定→解除を通り過ぎているはずの位置）
	// まで復元されていた場合、何もしないと「固定を経験していない」ときの
	// 見た目（上端基準）になってしまい、実際に上から順にスクロールした場合と
	// 背景の見た目が変わってしまう。初回判定でこれを検知し、通り過ぎた分を
	// boxOffsetYへ反映しておく。
	let initialized = false;

	// 固定していない間に使う、セクション自身を基準にしたbackground-position-y
	// （px）。CSS側の初期値は「center top」（＝0px相当）なので初期値は0。
	// 固定→解除するたびに、その時点の値へ更新される（CSSのキーワード/%値を
	// JSで読み戻す必要がないよう、常にpxの内部変数として持つ）。
	let boxOffsetY = 0;

	// 固定中に使う、画面（ビューポート）を基準にしたbackground-position-y
	// （px）。固定を開始する瞬間に1回だけ計算し、固定している間は一切
	// 更新しない。
	let frozenScreenY = 0;

	// 固定中だけ表示する、背景画像専用のオーバーレイ要素（body直下に1つだけ
	// 生成して再利用する）。position: fixedで画面に対して常に同じ位置に
	// 描画されるため、iOS Safariのbackground-attachment: fixedの制限を
	// 回避できる。
	let overlay = null;

	// このスクリプトがセクション自身の background-image / background-color を
	// 一時的に上書き（none / transparent）した状態かどうか。true のときだけ
	// hideOverlay() でそれらのインラインスタイルを除去する。フラグを見ずに
	// 無条件で removeProperty すると、PC幅（そもそも一度も engage() されていない
	// 状態）でも section.style.removeProperty('background-image') が実行され、
	// ブロックエディタの「背景画像」機能でセクション自身に設定されている
	// PC用の背景画像（このスクリプトが上書きしたものではない、元からの
	// インラインスタイル）まで消してしまい、PCで背景画像が表示されなくなる
	// 不具合があったため、このフラグで「自分が上書きした分だけ」元に戻すように
	// する。
	let sectionBgOverridden = false;

	function getOverlay() {
		if ( overlay ) {
			return overlay;
		}
		overlay = document.createElement( 'div' );
		overlay.setAttribute( 'aria-hidden', 'true' );
		overlay.style.position = 'fixed';
		overlay.style.top = '0';
		overlay.style.left = '0';
		overlay.style.width = '100%';
		overlay.style.height = '100%';
		overlay.style.zIndex = '-1';
		overlay.style.pointerEvents = 'none';
		overlay.style.backgroundRepeat = 'no-repeat';
		overlay.style.display = 'none';
		document.body.appendChild( overlay );
		return overlay;
	}

	// セクション自身の現在の背景画像・サイズ・繰り返し・横位置をそのまま
	// オーバーレイへコピーし、縦位置だけ指定のscreenY（ビューポート基準・px）
	// に設定して表示する。同時にセクション自身の背景画像は隠す（二重表示防止）。
	function showOverlayFixedAt( screenY ) {
		const computed = getComputedStyle( section );
		const bg = getOverlay();
		bg.style.backgroundImage = computed.backgroundImage;
		bg.style.backgroundSize = computed.backgroundSize;
		bg.style.backgroundColor = computed.backgroundColor;
		bg.style.backgroundRepeat = computed.backgroundRepeat;
		bg.style.backgroundPositionX = computed.backgroundPositionX;
		bg.style.backgroundPositionY = screenY + 'px';
		bg.style.display = 'block';

		// CSS側の初期値（center top !important）より優先させる必要が
		// あるため、important付きで設定する。
		// セクション自身は position: relative が指定されているため、
		// z-index: -1 のオーバーレイより手前（上）に描画される。
		// background-image を none にするだけでは、背景色のフォールバック
		// （background-color: #f1f1f1）がセクション自身のボックス背景として
		// 残ってしまい、結局オーバーレイを覆い隠してしまう。そのため
		// background-color も含めて完全に透明にし、セクションを
		// 「素通し」の状態にしてオーバーレイを見せる。
		section.style.setProperty( 'background-image', 'none', 'important' );
		section.style.setProperty( 'background-color', 'transparent', 'important' );
		sectionBgOverridden = true;
	}

	function hideOverlay() {
		if ( overlay ) {
			overlay.style.display = 'none';
		}
		// 自分が上書きしていた場合だけ元に戻す（詳細は sectionBgOverridden の
		// 宣言部コメント参照）。一度も engage() していない場合（PCのままページを
		// 開いた場合など）はここで何もしないため、セクション自身のもともとの
		// 背景画像（PC用にブロックエディタで設定されたインラインスタイル）に
		// 触れない。
		if ( sectionBgOverridden ) {
			section.style.removeProperty( 'background-image' );
			section.style.removeProperty( 'background-color' );
			sectionBgOverridden = false;
		}
	}

	function engage() {
		const sectionRect = section.getBoundingClientRect();
		// 現在画面上に実際に見えている背景のY位置（ビューポート基準）＝
		// セクション自身の現在位置 + そのときのbox基準オフセット。この値を
		// そのまま固定時のbackground-position-y（ビューポート基準）として
		// 使えば、切り替わる瞬間の見た目が変わらない。
		frozenScreenY = sectionRect.top + boxOffsetY;
		pinned = true;
		showOverlayFixedAt( frozenScreenY );
	}

	function release() {
		const sectionRect = section.getBoundingClientRect();
		// 固定していた画面上の位置（frozenScreenY）を、解除後の基準である
		// セクション自身の現在位置からの相対値に変換する。これにより解除した
		// 瞬間も画面上の見た目が変わらない。
		boxOffsetY = frozenScreenY - sectionRect.top;
		pinned = false;
		hideOverlay();
		section.style.setProperty( 'background-position-y', boxOffsetY + 'px', 'important' );
	}

	function teardown() {
		pinned = false;
		hideOverlay();
		section.style.removeProperty( 'background-position-y' );
		// boxOffsetYはリセットしない（SP幅に戻ったときも直前の見た目を保つ）。
	}

	function apply() {
		if ( ! isSpBreakpoint() ) {
			teardown();
			return;
		}

		const headerHeight = getHeaderHeight();
		const rect = scrollArea.getBoundingClientRect();

		if ( ! initialized ) {
			initialized = true;
			// 読み込み時点（スクロール位置の復元後）で、すでに.scroll-areaの
			// 下端がヘッダー下端を通り過ぎている（＝本来なら固定→解除を
			// 経験しているはずの位置まで最初から来ている）場合、その分を
			// boxOffsetYに反映しておく。固定されていた間に画面上で
			// 「進んだ」距離は、常に (.scroll-areaの高さ + ENGAGE_MARGIN) に
			// 等しい（固定開始はtopがheaderHeight+ENGAGE_MARGINの位置、解除は
			// bottomがheaderHeightの位置で起きるため）。
			if ( rect.bottom <= headerHeight ) {
				boxOffsetY = rect.height + ENGAGE_MARGIN;
				section.style.setProperty( 'background-position-y', boxOffsetY + 'px', 'important' );
			} else if ( rect.top <= headerHeight + ENGAGE_MARGIN ) {
				// 読み込み時点で、すでに固定範囲の「途中」（まだ解除には
				// 至っていない）まで来ている場合。この場合、通常のengage()を
				// そのまま呼ぶと、現在のrect.top（＝本来固定が始まる位置より
				// さらに下までスクロールされた後の位置）を基準にしてしまい、
				// 「そこから連続してスクロールしてきた場合」より手前の位置で
				// 固定されてしまう（固定中は画面上の位置が変わらないため、
				// 本来ならすでに (headerHeight + ENGAGE_MARGIN) の位置で
				// 固定されているはずである）。そのため、固定が本来
				// 発生していたはずの位置（headerHeight + ENGAGE_MARGIN。
				// この時点ではboxOffsetYは必ず0なのでそのまま基準にできる）を
				// 直接使って固定状態を組み立てる。
				pinned = true;
				frozenScreenY = headerHeight + ENGAGE_MARGIN + boxOffsetY;
				showOverlayFixedAt( frozenScreenY );
			}
		}

		// .scroll-areaの上端がヘッダー下端（+ ENGAGE_MARGIN分手前）に到達し、
		// かつ下端がまだヘッダー下端を通り過ぎていない間だけ、背景を固定する。
		const shouldPin = rect.top <= headerHeight + ENGAGE_MARGIN && rect.bottom > headerHeight;

		if ( shouldPin && ! pinned ) {
			engage();
		} else if ( ! shouldPin && pinned ) {
			release();
		}
		// 固定中・解除中のどちらでも、状態が変わらない限りは何もしない
		// （毎スクロールフレームでの再計算・再描画を避ける）。

		pinned = shouldPin;
	}

	// requestAnimationFrameでの間引きは行わない。scroll/resize等のハンドラ内で
	// 行っている処理はgetBoundingClientRect()の読み取り中心で軽いため、
	// 間引かずに毎回そのまま実行しても問題ない（以前rAFで間引いていたが、
	// 環境によってはrAFのコールバックがすぐに発火しない場合があり、
	// 固定処理そのものが動かなくなることがあったため、より確実な直接呼び出しに
	// 変更した）。

	// リロード直後は、ブラウザによるスクロール位置の復元や、ヘッダーの高さの
	// 計測（--header-heightを設定する別スクリプト）がまだ終わっていない
	// タイミングでこのスクリプトの最初の判定が行われてしまうことがある。
	// その場合、まだ確定していない古いスクロール位置・ヘッダー高さを基準に
	// 固定してしまい、その後の実際の位置とズレる（＝固定した瞬間の背景の
	// 位置がおかしくなる）。固定を開始する瞬間の値はそのまま使い切り、固定中は
	// 再計算しないため、この最初の判定が少しでも早すぎると後から自動的には
	// 直らない。そのため、初回の判定だけは少し待って（スクロール位置の復元・
	// レイアウトが安定してから）行う。
	const INITIAL_APPLY_DELAY = 150;

	function initialApply() {
		window.setTimeout( apply, INITIAL_APPLY_DELAY );
	}

	window.addEventListener( 'scroll', apply, { passive: true } );
	window.addEventListener( 'resize', apply );
	window.addEventListener( 'orientationchange', apply );
	window.addEventListener( 'load', initialApply );
	window.addEventListener( 'pageshow', initialApply );
	// スクリプト実行時点でただちに apply() は呼ばない。読み込み直後（特に
	// リロード時）はスクロール位置の復元やレイアウトの確定が済んでいない
	// ことがあり、その状態で最初の判定（initializedフラグの消費）が
	// 行われてしまうと、以降ずれたままになる。そのため最初の判定は必ず
	// initialApply()経由（少し待ってから）にする。
	initialApply();
} )();
